import React, { useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import Panel from "../../../components/ui/Panel/Panel.jsx";
import Banner from "../../../components/ui/Banner/Banner.jsx";
import Button from "../../../components/ui/Button/Button.jsx";
import FormField from "../../../components/forms/FormField/FormField.jsx";
import TextInput from "../../../components/forms/TextInput/TextInput.jsx";
import Select from "../../../components/forms/Select/Select.jsx";
import Checkbox from "../../../components/forms/Checkbox/Checkbox.jsx";
import ConfirmModal from "../../../components/ui/Modal/ConfirmModal.jsx";
import TriStateCheckbox from "../../../components/ui/TriStateCheckbox/TriStateCheckbox.jsx";
import BulkDeleteBar from "../../../components/ui/BulkDeleteBar/BulkDeleteBar.jsx";
import { useRowSelection } from "../../../hooks/useRowSelection.js";
import { getCreditReasons, getCreditAmount, getWeightCategories } from "../../../services/lookupService.js";
import { getCredits, saveCredits } from "../../../services/creditsService.js";
import { getToken } from "../../../services/api.js";
import { isValidVin, sanitizeVinInput, sanitizeExplanationInput } from "../../../utils/validators.js";
import { money, round2 } from "../../../utils/format.js";
import { useApp } from "../../../hooks/useApp.js";
import { useTotals } from "../../../hooks/useTotals.js";
import { RATES, WEIGHTS, MONTHS } from "../../../data/rates.js";
import { PRIOR_PERIOD_START, PRIOR_PERIOD_END, PRIOR_PERIOD_MIN_DATE, PRIOR_PERIOD_HINT } from "../../../utils/priorPeriod.js";

// Recomputes line5/line6 from the rows just saved and pushes them into
// state.returnTotals - saveCredits only returns the credit rows themselves
// (not the return's totals), so without this the Review page's "Credits"
// and "Balance due" lines would keep showing whatever line5/line6 were at
// the time TrucksStep last loaded, ignoring any credit just added/removed.
const totalsAfterCredits = (rows, line4) => {
  const line5 = round2(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0));
  return { line5, line6: round2(line4 - line5) };
};

const MONEY_PATTERN = /^\d{0,15}(\.\d{0,2})?$/;

// Matches the current backend seed data (lookup_credit_reasons - see
// CreditReasonOptionSeeder). Kept as the initial state so the step still
// renders immediately if the lookup fetch is slow or fails - these codes
// MUST stay in sync with the seeder, since CreditsServiceImpl validates
// reasonCd against that same lookup table.
const DEFAULT_CREDIT_REASONS = [
  ["sold", "Sold, destroyed, or stolen before June 1"],
  ["mileage", "Used 5,000 miles or less (7,500 agricultural) last period"],
];

// Line 5's date field is labeled differently depending on why the credit is
// being claimed - same as the wireframe's per-reason date label.
const DATE_LABELS = { sold: "Date sold/destroyed/stolen", mileage: "Date last used" };

// Line 5 credits refund tax already paid on a vehicle's PRIOR Form 2290 (the
// filing period a year before the return currently being filed) - NOT this
// return's own first-used month/period. PRIOR_PERIOD_START/END come from
// ../../../utils/priorPeriod.js (shared with PriorYearStep's Line 9, which
// concerns the same prior period).

// Months remaining in the PRIOR period once the disposal month itself is
// accounted for - Form 2290 treats a partial month as a full month, so the
// disposal month still counts as "used" (e.g. sold in August -> credited
// starting September, 10 months remain). Clamped to [0, 11]; null if
// dateStr is missing/outside the prior period.
const monthsRemainingAfterDisposal = (dateStr) => {
  if (!dateStr) return null;
  const [py, pm] = PRIOR_PERIOD_START.split("-").map(Number);
  const [dy, dm] = dateStr.split("-").map(Number);
  if (!dy || !dm) return null;
  const offset = dy * 12 + dm - (py * 12 + pm);
  if (offset < 0 || offset > 11) return null;
  return Math.max(0, 11 - offset);
};

// Local fallback only - the authoritative credit amount comes from GET
// /lookups/credit-amount (server-side, from the partial_period_rate table;
// see the effect below). This is used only if that call fails (offline/demo)
// so the field still populates: the annual rate for the category/logging
// combo, times the months remaining in the prior period after the vehicle
// stopped being used, divided by 12 - e.g. sold in August (prior period) ->
// credit = annual × 10/12.
function creditAmountFor(cat, logging, dateStr, weightCategoriesLookup) {
  if (!cat) return null;
  const remaining = monthsRemainingAfterDisposal(dateStr);
  if (remaining == null) return null;
  const looked = weightCategoriesLookup.find((w) => w.code === cat);
  const annual = looked
    ? Number(logging ? looked.annualLoggingAmt : looked.annualNormalAmt)
    : RATES[cat]?.[logging ? 1 : 0] ?? 0;
  return round2((annual * remaining) / 12);
}

// "mileage" credits (5,000 miles or less / 7,500 agricultural last period)
// use the partial-period rate for the month actually picked (e.g. a date in
// August shows August's own rate) rather than the "sold" reason's
// month-after-disposal proration, since the mileage allowance is evaluated
// for the month the vehicle was last used, not the month after.
const monthLabelFor = (dateStr) => {
  if (!dateStr) return "";
  const [y, m] = dateStr.split("-").map(Number);
  if (!y || !m) return "";
  return new Date(y, m - 1, 1).toLocaleDateString("en-US", { month: "long" });
};

// /lookups/weight-categories?firstUsedMonth= expects a month within the
// CURRENT return's own filing period (MONTHS' "2026-07".."2027-06"), but a
// credit's date lives in the PRIOR period (e.g. "2025-09") - passing that
// raw date's year desyncs the backend's month-in-cycle math (it returns the
// wrong row, e.g. August's rate for a September date). Normalizing to the
// canonical in-cycle month string here reuses the exact same value
// TrucksStep already sends for "month first used", sidestepping that.
const canonicalMonthFor = (dateStr) => {
  if (!dateStr) return null;
  const monthNum = Number(dateStr.slice(5, 7));
  if (!monthNum) return null;
  return MONTHS.find(([value]) => Number(value.slice(5, 7)) === monthNum)?.[0] || null;
};

let nextRowId = 1;

// Form 2290 Line 5 credits - add-form-then-table entry, same pattern as
// TrucksStep: a row is only added once the draft form validates, then it's
// read-only in the table below. The amount auto-calculates from the weight
// category/logging rate and how many months of the period the vehicle was
// actually used before the date entered (still editable if the filer needs
// to adjust it).
const CreditsStep = ({ onNext, onBack }) => {
  const { state, dispatch, showToast } = useApp();
  const t = useTotals();
  const [rows, setRows] = useState([]);
  const [creditReasons, setCreditReasons] = useState(DEFAULT_CREDIT_REASONS);
  const [creditReasonsLoading, setCreditReasonsLoading] = useState(true);
  const [loadingCredits, setLoadingCredits] = useState(true);
  const [saving, setSaving] = useState(false);
  const [amountLoading, setAmountLoading] = useState(false);

  const [draft, setDraft] = useState({ vin: "", cat: "", logging: false, reason: "", date: "", explanation: "", amount: "" });
  const [draftErrors, setDraftErrors] = useState({});
  const [editingId, setEditingId] = useState(null);
  const isEditing = editingId != null;
  const [pendingRemove, setPendingRemove] = useState(null);
  const [removeError, setRemoveError] = useState("");

  // Bulk-select, same useRowSelection hook every other table with
  // checkbox bulk-select shares (Trucks, VIN corrections, Prior year, etc.).
  const selection = useRowSelection();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkError, setBulkError] = useState("");

  const usingBackend = !!(getToken() && state.returnId);

  // Reloads previously saved credits when the step is (re)entered with a
  // backend return - without this, revisiting Credits after leaving the
  // wizard would show an empty table even though rows were already saved.
  useEffect(() => {
    if (!usingBackend) {
      setLoadingCredits(false);
      return;
    }
    let cancelled = false;
    getCredits(state.returnId)
      .then((saved) => {
        if (!cancelled) setRows(saved);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setLoadingCredits(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.returnId]);

  const catOptions = state.weightCategoriesLookup.length
    ? state.weightCategoriesLookup.map((w) => [
        w.code,
        `${w.code} · ${w.weightLabel} · ${money(draft.logging ? w.annualLoggingAmt : w.annualNormalAmt)}/yr`,
      ])
    : Object.keys(RATES).map((c) => [c, `${c} · ${WEIGHTS[c]} · ${money(RATES[c][draft.logging ? 1 : 0])}/yr`]);

  const updateDraft = (patch) => {
    if (patch.amount !== undefined) {
      const raw = patch.amount.replace(/[^\d.]/g, "");
      const parts = raw.split(".");
      if (parts.length > 2) return;
      const whole = parts[0]?.slice(0, 15) || "";
      const cents = parts[1]?.slice(0, 2) || "";
      const normalized = parts.length === 2 ? `${whole}.${cents}` : whole;
      if (!MONEY_PATTERN.test(normalized)) return;
      patch = { ...patch, amount: normalized };
    }
    if (patch.date !== undefined && patch.date !== "") {
      // Credits belong to the vehicle's PRIOR return, so the date must fall
      // within that prior filing period - clamp outright rather than let an
      // out-of-range date (covers native date-picker selection as well as
      // any manual typing the input allows) reach the credit-amount call.
      if (patch.date > PRIOR_PERIOD_END) patch = { ...patch, date: PRIOR_PERIOD_END };
      else if (patch.date < PRIOR_PERIOD_MIN_DATE) patch = { ...patch, date: PRIOR_PERIOD_MIN_DATE };
    }
    setDraft((d) => ({ ...d, ...patch }));
    setDraftErrors((errs) => ({ ...errs, ...Object.fromEntries(Object.keys(patch).map((k) => [k, undefined])) }));
  };

  // Recomputes the credit amount whenever the reason, category, logging
  // flag, or date changes:
  //  - "mileage": the partial-period rate for the month actually picked
  //    (GET /lookups/weight-categories?firstUsedMonth=..., same table
  //    TrucksStep reads for "month first used") - falls back to the RATES
  //    annual figure if the call fails.
  //  - anything else ("sold"/default): the existing month-after-disposal
  //    proration via GET /lookups/credit-amount, falling back to the local
  //    annual/12 proration if that call fails.
  useEffect(() => {
    if (!draft.cat || !draft.date) return;
    let cancelled = false;
    setAmountLoading(true);

    if (draft.reason === "mileage") {
      const month = canonicalMonthFor(draft.date);
      getWeightCategories(month)
        .then((weightCategories) => {
          if (cancelled) return;
          const row = Array.isArray(weightCategories) ? weightCategories.find((w) => w.code === draft.cat) : null;
          const amt = row
            ? Number(draft.logging ? row.periodLoggingAmt ?? row.annualLoggingAmt : row.periodNormalAmt ?? row.annualNormalAmt)
            : RATES[draft.cat]?.[draft.logging ? 1 : 0] ?? 0;
          if (Number.isFinite(amt)) setDraft((d) => ({ ...d, amount: amt.toFixed(2) }));
        })
        .catch(() => {
          const amt = RATES[draft.cat]?.[draft.logging ? 1 : 0] ?? 0;
          setDraft((d) => ({ ...d, amount: amt.toFixed(2) }));
        })
        .finally(() => {
          if (!cancelled) setAmountLoading(false);
        });
      return () => {
        cancelled = true;
      };
    }

    getCreditAmount({ category: draft.cat, logging: draft.logging, creditDate: draft.date })
      .then((amt) => {
        if (!cancelled && Number.isFinite(amt)) setDraft((d) => ({ ...d, amount: amt.toFixed(2) }));
      })
      .catch(() => {
        const amt = creditAmountFor(draft.cat, draft.logging, draft.date, state.weightCategoriesLookup);
        if (!cancelled && amt != null) setDraft((d) => ({ ...d, amount: amt.toFixed(2) }));
      })
      .finally(() => {
        if (!cancelled) setAmountLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.cat, draft.logging, draft.date, draft.reason]);

  const resetDraft = () => {
    setDraft({ vin: "", cat: "", logging: false, reason: "", date: "", explanation: "", amount: "" });
    setDraftErrors({});
    setEditingId(null);
  };

  const startEdit = (row) => {
    setDraft({
      vin: row.vin,
      cat: row.cat || "",
      logging: !!row.logging,
      reason: row.reason,
      date: row.date,
      explanation: row.explanation || "",
      amount: row.amount,
    });
    setDraftErrors({});
    setEditingId(row.id);
  };

  const addCredit = async () => {
    const v = draft.vin.trim().toUpperCase();
    const errs = {};
    if (!draft.reason) errs.reason = "Pick a reason.";
    if (!isValidVin(v)) errs.vin = "Enter a valid VIN.";
    else if (rows.some((row) => row.vin === v && row.id !== editingId)) {
      // Business Rule F2290-006 - each vehicle can only be claimed once.
      errs.vin = "You've already claimed a credit for this VIN.";
    }
    if (!draft.cat) errs.cat = "Pick a weight category.";
    if (!draft.date) errs.date = "Date is required.";
    if (!draft.explanation || !draft.explanation.trim()) errs.explanation = "Explanation is required.";
    if (!draft.amount || Number(draft.amount) <= 0) errs.amount = "Enter the credit amount.";

    const entry = {
      vin: v,
      cat: draft.cat,
      logging: draft.logging,
      reason: draft.reason,
      date: draft.date,
      explanation: draft.explanation.trim(),
      amount: draft.amount,
    };

    const nextRows = isEditing
      ? rows.map((row) => (row.id === editingId ? { ...entry, id: editingId } : row))
      : [...rows, { ...entry, id: nextRowId++ }];

    // Business Rule F2290-004 - credits can never exceed the tax owed
    // (Line 4); the backend doesn't reject this at save time, so it's
    // checked here before it ever reaches the wire.
    if (!errs.amount) {
      const totalCredits = round2(nextRows.reduce((s, r) => s + (Number(r.amount) || 0), 0));
      if (totalCredits > t.line4) {
        errs.amount = `Total credits (${money(totalCredits)}) can't be more than the tax owed (${money(t.line4)}).`;
      }
    }

    setDraftErrors(errs);
    if (Object.keys(errs).length) return;

    if (usingBackend) {
      setSaving(true);
      try {
        const saved = await saveCredits(state.returnId, nextRows);
        setRows(saved);
        dispatch({ type: "SET_TOTALS", patch: totalsAfterCredits(saved, t.line4) });
        resetDraft();
      } catch (err) {
        showToast(err.message || "Couldn't save the credit. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    setRows(nextRows);
    resetDraft();
  };

  const removeCredit = (id) => {
    const row = rows.find((r) => r.id === id);
    if (!row) return;
    setRemoveError("");
    setPendingRemove(row);
  };

  const cancelRemoveCredit = () => {
    setPendingRemove(null);
    setRemoveError("");
  };

  const confirmRemoveCredit = async () => {
    const id = pendingRemove?.id;
    if (id == null) return;
    const nextRows = rows.filter((row) => row.id !== id);

    if (usingBackend) {
      setSaving(true);
      try {
        const saved = await saveCredits(state.returnId, nextRows);
        setRows(saved);
        dispatch({ type: "SET_TOTALS", patch: totalsAfterCredits(saved, t.line4) });
        if (editingId === id) resetDraft();
        setPendingRemove(null);
        setRemoveError("");
      } catch (err) {
        setRemoveError(err.message || "Couldn't remove the credit. Please try again.");
      } finally {
        setSaving(false);
      }
      return;
    }

    setRows(nextRows);
    if (editingId === id) resetDraft();
    setPendingRemove(null);
    setRemoveError("");
  };

  // Bulk delete - saveCredits is a full replace (no per-row DELETE endpoint,
  // same as the single-row path above), so this just re-saves the table
  // minus the selected rows. Always confirms first regardless of mode, same
  // guardrail TrucksStep's bulk delete applies (removing more than one row
  // at once).
  const openBulkDelete = () => {
    setBulkError("");
    setBulkConfirmOpen(true);
  };

  const cancelBulkDelete = () => {
    setBulkConfirmOpen(false);
    setBulkError("");
  };

  const confirmBulkDelete = async () => {
    const count = selection.count;
    const nextRows = rows.filter((row) => !selection.selectedIds.has(row.id));

    setBulkDeleting(true);
    try {
      if (usingBackend) {
        const saved = await saveCredits(state.returnId, nextRows);
        setRows(saved);
        dispatch({ type: "SET_TOTALS", patch: totalsAfterCredits(saved, t.line4) });
      } else {
        setRows(nextRows);
      }
      if (editingId != null && selection.selectedIds.has(editingId)) resetDraft();
      selection.clear();
      setBulkConfirmOpen(false);
      setBulkError("");
      showToast(`${count} credit${count === 1 ? "" : "s"} deleted successfully.`);
    } catch (err) {
      // Keep the selection intact on failure, same as the single-row branch above.
      setBulkError(err.message || "Unable to delete the selected credits. Please try again.");
    } finally {
      setBulkDeleting(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    getCreditReasons()
      .then((reasons) => {
        if (cancelled) return;
        if (Array.isArray(reasons) && reasons.length) {
          setCreditReasons(reasons.map((r) => [r.code, r.label]));
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setCreditReasonsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Sum of every row currently in the table - recalculates on every render,
  // so it's always current after an add/edit/delete or after loading saved
  // credits (rows changing is what drives this, not a separate effect).
  const totalCredits = round2(rows.reduce((s, r) => s + (Number(r.amount) || 0), 0));
  // t.creditsExceedTax (useTotals.js) is the single source of truth shared
  // with ReviewStep - it flips true the moment line5 (credits) exceeds line4
  // (total tax), including when a truck edited on a prior visit to Trucks
  // lowered line4 below credits already saved here.
  const creditsInvalid = t.creditsExceedTax;

  return (
    <Panel padded>
      <div className="font-disp uppercase tracking-[.14em] text-xs text-[var(--accent)] font-semibold">
         Credits
      </div>
      <h2 className="text-2xl m-[4px_0]">Any credits to claim?</h2>
      <p className="text-[var(--muted)] max-w-[62ch] mb-4">
        Most filers have none. Add one only if you already paid tax on a vehicle that was{" "}
        <b>sold, destroyed, or stolen before June 1</b> (and not used after), or that was{" "}
        <b>used 5,000 miles or less</b> (7,500 for agricultural) last period. We attach the details as the
        IRS-required Credits Amount Statement.
      </p>

      {creditsInvalid && (
        <Banner tone="danger" icon="⚠️" className="mb-4">
          Total Credits cannot exceed the Total Tax amount. Please reduce the credit amount or update your
          vehicle information.
        </Banner>
      )}

      <Panel tone="soft" className="!shadow-none p-[18px] mb-3">
        <FormField label="Why are you claiming this credit?" required error={draftErrors.reason} showError={!!draftErrors.reason}>
          <Select
            value={draft.reason}
            onChange={(e) => updateDraft({ reason: e.target.value })}
            disabled={creditReasonsLoading}
            invalid={!!draftErrors.reason}
          >
            <option value="">Select a reason…</option>
            {creditReasons.map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
        </FormField>

        <div className="grid gap-3.5 sm:grid-cols-2">
          <FormField label="VIN" required error={draftErrors.vin} showError={!!draftErrors.vin}>
            <TextInput
              mono
              maxLength={19}
              placeholder="Vehicle the tax was paid on"
              value={draft.vin}
              onChange={(e) => updateDraft({ vin: sanitizeVinInput(e.target.value) })}
              invalid={!!draftErrors.vin}
            />
          </FormField>
                <FormField label="Weight category" required error={draftErrors.cat} showError={!!draftErrors.cat}>
            <Select value={draft.cat} onChange={(e) => updateDraft({ cat: e.target.value })} invalid={!!draftErrors.cat}>
              <option value="">Select weight category…</option>
              {catOptions.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormField>
        </div>

        <div className="grid gap-3.5 sm:grid-cols-2">
        <FormField
            label={DATE_LABELS[draft.reason] || "Date"}
            required
            error={draftErrors.date}
            showError={!!draftErrors.date}
            hint={PRIOR_PERIOD_HINT}
          >
            <TextInput
              type="date"
              min={PRIOR_PERIOD_MIN_DATE}
              max={PRIOR_PERIOD_END}
              value={draft.date}
              onChange={(e) => updateDraft({ date: e.target.value })}
              invalid={!!draftErrors.date}
            />
          </FormField>
        </div>

        <Checkbox
          id="cLog"
          title="Logging truck"
          subtitle="Hauls harvested forest products — the credit uses the logging rate instead of the standard rate."
          checked={draft.logging}
          onChange={(e) => updateDraft({ logging: e.target.checked })}
        />

        <FormField label="Explanation" required error={draftErrors.explanation} showError={!!draftErrors.explanation}>
          <TextInput
            placeholder="Briefly explain the circumstances"
            maxLength={500}
            value={draft.explanation}
            onChange={(e) => updateDraft({ explanation: sanitizeExplanationInput(e.target.value) })}
            invalid={!!draftErrors.explanation}
          />
        </FormField>

        <FormField
          label="Credit amount"
          required
          error={draftErrors.amount}
          showError={!!draftErrors.amount}
          hint={
            amountLoading
              ? "Calculating…"
              : draft.cat && draft.date
              ? draft.reason === "mileage"
                ? `Auto-calculated: ${monthLabelFor(draft.date)}'s rate for the weight category above.`
                : `Auto-calculated: ${monthsRemainingAfterDisposal(draft.date) ?? 0} of 12 months unused after the date above.`
              : "Pick a weight category and date above to auto-calculate this amount."
          }
        >
          <TextInput
            mono
            inputMode="decimal"
            placeholder="0.00"
            maxLength={18}
            value={draft.amount}
            readOnly
            invalid={!!draftErrors.amount}
          />
        </FormField>

        <div className="flex gap-2">
          <Button variant="ghost" onClick={addCredit} disabled={saving}>
            {saving ? "Saving…" : isEditing ? "Update credit" : "+ Add a credit"}
          </Button>
          {isEditing && (
            <Button variant="ghost" onClick={resetDraft} disabled={saving}>
              Cancel
            </Button>
          )}
        </div>
      </Panel>

      {!loadingCredits && rows.length > 0 && (
        <BulkDeleteBar count={selection.count} onDelete={openBulkDelete} loading={bulkDeleting} />
      )}

      <div className="overflow-x-auto mb-3">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            {!loadingCredits && rows.length > 0 && (
              <th className="text-left p-3 border-b border-[var(--line)] w-[36px]">
                <TriStateCheckbox
                  checked={rows.length > 0 && selection.count === rows.length}
                  indeterminate={selection.count > 0 && selection.count < rows.length}
                  onChange={() => selection.selectAll(selection.count === rows.length ? [] : rows.map((r) => r.id))}
                  label="Select all credits"
                />
              </th>
            )}
            {["#", "Reason", "VIN", "Category", "Date", "Amount", "Actions"].map((h) => (
              <th
                key={h}
                className="text-left p-3 border-b border-[var(--line)] font-disp uppercase tracking-[.07em] text-[11px] text-[var(--muted)] font-semibold"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loadingCredits ? (
            <tr>
              <td colSpan={7} className="text-[var(--muted)] p-[22px_12px]">
                Loading your saved credits…
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={7} className="text-[var(--muted)] p-[22px_12px]">
                No credits yet. Most filers have none — add one above only if it applies to you.
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr key={row.id} className="hover:bg-[var(--accent-soft)] transition-colors">
                <td className="text-left p-3 border-b border-[var(--line)]">
                  <TriStateCheckbox
                    checked={selection.selectedIds.has(row.id)}
                    onChange={() => selection.toggle(row.id)}
                    label={`Select row ${i + 1}`}
                  />
                </td>
                <td className="text-left p-3 border-b border-[var(--line)]">{i + 1}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">
                  {creditReasons.find(([value]) => value === row.reason)?.[1] || row.reason}
                </td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{row.vin}</td>
                <td className="text-left p-3 border-b border-[var(--line)]">
                  {row.cat || "—"}
                  {row.logging && <span className="text-[var(--muted)]"> · logging</span>}
                </td>
                <td className="text-left p-3 border-b border-[var(--line)]">{row.date}</td>
                <td className="text-left p-3 border-b border-[var(--line)] font-mono text-[13px]">{money(Number(row.amount) || 0)}</td>
                <td className="text-right p-3 border-b border-[var(--line)] whitespace-nowrap">
                  <button
                    type="button"
                    title="Edit"
                    onClick={() => startEdit(row)}
                    disabled={saving}
                    className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 hover:text-[var(--accent)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Pencil size={14} />
                  </button>
                  <button
                    type="button"
                    title="Delete"
                    onClick={() => removeCredit(row.id)}
                    disabled={saving}
                    className="inline-flex items-center justify-center border-0 bg-transparent text-[var(--dim)] cursor-pointer p-1 ml-1 hover:text-[var(--danger)] disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      </div>

      <div className="flex justify-between items-baseline mb-3 pt-3 border-t-2 border-[var(--ink)]">
        <span className="font-disp uppercase tracking-[.08em] text-[13px]">Total Credits Amount</span>
        <b className="font-mono text-xl font-semibold">{money(totalCredits)}</b>
      </div>

      <div className="flex justify-between gap-3 mt-6">
        <Button variant="ghost" onClick={onBack}>
          ← Back
        </Button>
        <div className="flex gap-3">
          <Button variant="ghost" onClick={onNext} disabled={saving || creditsInvalid}>
            Skip this step →
          </Button>
          <Button onClick={onNext} disabled={saving || creditsInvalid}>Continue to summary →</Button>
        </div>
      </div>

      <ConfirmModal
        open={!!pendingRemove}
        title="Delete Confirmation"
        confirmLabel="Delete"
        loading={saving}
        error={removeError}
        onConfirm={confirmRemoveCredit}
        onCancel={cancelRemoveCredit}
      >
        Are you sure you want to delete this record? This action cannot be undone.
      </ConfirmModal>

      <ConfirmModal
        open={bulkConfirmOpen}
        title="Delete Selected Records?"
        confirmLabel="Delete"
        loading={bulkDeleting}
        error={bulkError}
        onConfirm={confirmBulkDelete}
        onCancel={cancelBulkDelete}
      >
        Are you sure you want to delete these {selection.count} record{selection.count === 1 ? "" : "s"}? This action
        cannot be undone.
      </ConfirmModal>
    </Panel>
  );
};

export default CreditsStep;
